import { TimeStamp } from "@core-ts/TimeStamp"
import { context } from "./Context"
// import type { LogEntry } from "@core-proto/proto/LogService/LogEntry"
import { DisposableQueue } from "@core-ts/Queue/DisposableQueue"

export const LEVELS = ["INFO", "ERROR", "WARN", "DEBUG", "PERF", "STAT", "ACCESS", "TRACE"] as const
export type LogLevel = (typeof LEVELS)[number]

export type Logger = {
    [L in Lowercase<LogLevel>]: (message: string | unknown, e?: Error | null) => void
}

export const Log = {} as Logger
const queue = new DisposableQueue<unknown>({
    batchSize: [8, Number.POSITIVE_INFINITY],
    maxLength: 10000,
    maxLengthCallback(count) {
        console.log(`[WARN] Discarding ${count} entries`)
    },
})
let isSending = false

// TODO: Log doesn't properly handle cases where a context is fully or partially unavailable
for (const level of LEVELS) {
    Log[level.toLowerCase() as Lowercase<LogLevel>] = (message: string | unknown, e: Error | null = null) => {
        context.log(level, message, e)

        if (context.haveConfig && context.haveLogger) {
            const logConfig = context.config.subConfigs.get("log")
            if (!logConfig || logConfig.getString("log-service", "none") === "none") return
            const logApiKey = logConfig?.getString("apiKey", "") ?? ""
            if (logApiKey.length === 0) return

            const meta = context.getLogMeta()

            const entry: Record<string, unknown> = {
                timeStamp: { timestamp: TimeStamp.now().toLong() },
                service: meta.application,
                level,
                message,
            }

            if (meta.request) entry.requestId = meta.request
            if (meta.instance) entry.instance = meta.instance
            if (meta.userId) entry.userId = meta.userId
            if (meta.userEmail) entry.userEmail = meta.userEmail
            if (meta.internalSerial) entry.internalSerial = meta.internalSerial
            if (e) entry.message = `${e.message} ${e.name} ${e.stack}`

            queue.push(entry)

            const batch = queue.tryNext()
            if (batch) {
                // This is a function that is only used to fire off async code without caring for the results, therefor we void it
                void (async () => {
                    isSending = true

                    try {
                        await context.loginApiKey(logApiKey).catch(e => {
                            throw e
                        })
                        await context.logger.LogV(batch).catch((e: Error) => {
                            throw e
                        })
                    } catch (e) {
                        // This code runs in very early startup which means a connection to the log service might not have been established yet
                        queue.push(...batch)
                    }

                    isSending = false
                })()
            }
        }
    }
}
